/**
 * Batch numbers typed by storekeepers.
 *
 * Waybills print them without leading zeros, so a batch that is "0000313269"
 * on paper gets typed as "313269". The backend rescues that, but only after
 * checking the value as sent: a batch the gateway already knows is stored
 * byte-identical and never rewritten. This mirrors that order, so the client
 * can never turn a value the backend would have accepted into one it rejects.
 */

export const BATCH_NUMBER_LENGTH = 10;
export const BATCH_NUMBER_PATTERN = /^\d{10}$/;

export const BYPASS_VALUES = new Set(["unknown", "ineligible"]);

// Letter-for-digit typos seen in production data. p and u are here because
// "000p161904" and "00001u5742" only read as 0-substitutions once you see
// where they sit in an otherwise all-digit value.
const LETTER_TO_DIGIT: Record<string, string> = {
  o: "0",
  O: "0",
  p: "0",
  P: "0",
  u: "0",
  U: "0",
  l: "1",
  L: "1",
  i: "1",
  I: "1",
};

/** The canonical 10-digit form, or null when the value cannot become one. */
export const canonicalBatchNumber = (input: string): string | null => {
  const untypoed = input
    .trim()
    .split("")
    .map((char) => LETTER_TO_DIGIT[char] ?? char)
    .join("");

  if (!untypoed || !/^\d+$/.test(untypoed)) return null;

  const significant = untypoed.replace(/^0+/, "");
  if (significant.length > BATCH_NUMBER_LENGTH) return null;

  return significant.padStart(BATCH_NUMBER_LENGTH, "0");
};

/**
 * What to put in the payload for a value the user typed.
 *
 * Canonicalizes only when the result is a batch we can see in the loaded
 * options; otherwise the value goes through untouched and the backend decides.
 * Rewriting on a hunch would break exactly the batches that are valid but not
 * in the fetched page.
 */
export const resolveBatchNumber = (
  input: string,
  knownBatchNumbers: ReadonlySet<string> = new Set()
): string => {
  const trimmed = input.trim();

  if (BYPASS_VALUES.has(trimmed.toLowerCase())) return trimmed.toLowerCase();
  if (!trimmed || knownBatchNumbers.has(trimmed)) return trimmed;

  const canonical = canonicalBatchNumber(trimmed);
  return canonical && knownBatchNumbers.has(canonical) ? canonical : trimmed;
};

export const isValidBatchNumberFormat = (value: string): boolean =>
  BATCH_NUMBER_PATTERN.test(value.trim()) ||
  BYPASS_VALUES.has(value.trim().toLowerCase());
